// src/components/VariantToast.jsx
import React, { useEffect, useMemo, useCallback, useState } from "react";
import { FiUploadCloud, FiFileText, FiX } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import Dropzone from "react-dropzone";

import { uploadAFile, deleteAFile } from "../app/features/upload/uploadSlice";
import "../pages/styles/variantToast.css";

const DEFAULT_PRINT_SPEC = {
  allowedExtensions: ["pdf", "png", "jpg", "jpeg", "cdr", "ai"],
  maxFileSizeMB: 10,
  instructions: "",
};

const stripMeasurements = (val) => {
  const s = String(val || "").trim();
  if (!s) return "";
  return s
    .replace(/\s*\([^)]*\b(mm|cm|in|inch|inches)\b[^)]*\)\s*$/i, "")
    .replace(/\s*\b(mm|cm|in|inch|inches)\b\s*$/i, "")
    .trim();
};

const formatUGX = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "UGX 0";
  return `UGX ${num.toLocaleString()}`;
};

const VariantToast = ({
  open,
  onClose,
  productTitle = "Product",

  sizes = [],
  colors = [],
  colorMap = {},

  initialSize = "",
  initialColorId = null,

  requireSize = true,
  requireColor = true,

  isPrintable = false,
  printSpec,

  requireArtwork = false,

  // ✅ print pricing record
  // expected:
  // { id, title, preparePrice, discountMinQty, printPrice:{oneSide,twoSide}, printPriceDiscount:{oneSide,twoSide} }
  printPricing = null,
  requirePrintSide = false,
  initialPrintSide = "",
  prepareAlreadyApplied = false,

  // ✅ NEW: qty passed from SingleProduct so toast can show discount unit price correctly
  qty = 1,

  onConfirm,
}) => {
  const dispatch = useDispatch();

  const uploadState = useSelector(
    (state) => state?.upload || state?.uploads || state?.files || {}
  );
  const uploading = Boolean(uploadState?.uploading || uploadState?.isLoading);
  const uploadProgress = Number(uploadState?.uploadProgress ?? 0);
  const uploadErrorMsg = String(uploadState?.message || "");

  const mergedPrintSpec = useMemo(
    () => ({ ...DEFAULT_PRINT_SPEC, ...(printSpec || {}) }),
    [printSpec]
  );

  const [size, setSize] = useState(stripMeasurements(initialSize || ""));
  const [colorId, setColorId] = useState(initialColorId || null);

  // ✅ print side selection
  const [printSide, setPrintSide] = useState(initialPrintSide || ""); // "oneSide" | "twoSide" | ""

  // null = not answered yet, true = yes, false = no
  const [hasArtwork, setHasArtwork] = useState(null);

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [artworkError, setArtworkError] = useState("");
  const [printInstructions, setPrintInstructions] = useState("");

  // ✅ (optional but recommended) lock background scroll when toast is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setSize(stripMeasurements(initialSize || ""));
    setColorId(initialColorId || null);
    setPrintSide(initialPrintSide || "");
    setHasArtwork(null);
    setUploadedFiles([]);
    setArtworkError("");
    setPrintInstructions("");
  }, [open, initialSize, initialColorId, initialPrintSide]);

  const colorObj = useMemo(() => {
    return (colors || []).find((c) => String(c?.id) === String(colorId)) || null;
  }, [colors, colorId]);

  const allowedExts = useMemo(() => {
    const exts = Array.isArray(mergedPrintSpec?.allowedExtensions)
      ? mergedPrintSpec.allowedExtensions
      : [];
    return exts.map((x) => String(x).toLowerCase().trim()).filter(Boolean);
  }, [mergedPrintSpec]);

  const acceptAttr = useMemo(() => allowedExts.map((x) => `.${x}`).join(","), [allowedExts]);

  const maxMB = useMemo(() => {
    const n = Number(mergedPrintSpec?.maxFileSizeMB);
    return Number.isFinite(n) && n > 0 ? n : 10;
  }, [mergedPrintSpec]);

  const validateArtworkFile = useCallback(
    (file) => {
      if (!file) return "";
      const name = String(file.name || "");
      const ext = name.includes(".") ? name.split(".").pop().toLowerCase() : "";
      const sizeMB = file.size ? file.size / (1024 * 1024) : 0;

      if (allowedExts.length && !allowedExts.includes(ext)) {
        return `Invalid file type .${ext}. Allowed: ${allowedExts.join(", ")}`;
      }
      if (maxMB && sizeMB > maxMB) {
        return `File too large (${sizeMB.toFixed(1)}MB). Max allowed: ${maxMB}MB`;
      }
      return "";
    },
    [allowedExts, maxMB]
  );

  const showUploadUI = Boolean(isPrintable && hasArtwork === true);
  const mustUploadFile = Boolean(isPrintable && hasArtwork === true && requireArtwork);

  const sizesForUI = useMemo(() => {
    const arr = Array.isArray(sizes) ? sizes : [];
    const out = arr
      .map((s) => {
        if (typeof s === "string") return stripMeasurements(s);
        return stripMeasurements(s?.labelWithMM || s?.label || s?.name || "");
      })
      .map((x) => x.trim())
      .filter(Boolean);

    return Array.from(new Set(out));
  }, [sizes]);

  const hasPrintPricing = useMemo(() => Boolean(printPricing?.title), [printPricing]);

  const printPreparePrice = useMemo(() => {
    if (!hasPrintPricing) return 0;
    return Number(printPricing?.preparePrice ?? 0) || 0;
  }, [hasPrintPricing, printPricing]);

  const printDiscountMinQty = useMemo(() => {
    const n = Number(printPricing?.discountMinQty);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [printPricing]);

  const printDiscountApplies = useMemo(() => {
    if (!hasPrintPricing) return false;
    if (!printDiscountMinQty) return false;
    return Number(qty || 1) >= Number(printDiscountMinQty);
  }, [hasPrintPricing, qty, printDiscountMinQty]);

  // ✅ unit print price for the chosen side (shows discount unit when qty qualifies)
  const printUnit = useMemo(() => {
    if (!hasPrintPricing || !printSide) return 0;

    const base = Number(printPricing?.printPrice?.[printSide] ?? 0) || 0;
    const disc = Number(printPricing?.printPriceDiscount?.[printSide] ?? 0) || 0;

    if (printDiscountApplies && Number.isFinite(disc) && disc > 0) return disc;
    return base;
  }, [hasPrintPricing, printPricing, printSide, printDiscountApplies]);

  const canConfirm = useMemo(() => {
    if (requireSize && !size) return false;
    if (requireColor && !colorObj) return false;

    if (requirePrintSide && hasPrintPricing && !printSide) return false;

    if (isPrintable && hasArtwork === null) return false;

    if (mustUploadFile) {
      if (!Array.isArray(uploadedFiles) || uploadedFiles.length === 0) return false;
    }
    return true;
  }, [
    requireSize,
    size,
    requireColor,
    colorObj,
    requirePrintSide,
    hasPrintPricing,
    printSide,
    isPrintable,
    hasArtwork,
    mustUploadFile,
    uploadedFiles,
  ]);

  const onOverlayMouseDown = useCallback(
    (e) => {
      if (e.target.classList.contains("vt-overlay")) onClose?.();
    },
    [onClose]
  );

  useEffect(() => {
    if (hasArtwork === false) {
      setUploadedFiles([]);
      setArtworkError("");
      setPrintInstructions("");
    }
  }, [hasArtwork]);

  const handleFileDrop = useCallback(
    (acceptedFiles) => {
      if (!acceptedFiles?.length) return;

      const errors = acceptedFiles.map((f) => validateArtworkFile(f)).filter(Boolean);
      if (errors.length) {
        setArtworkError(errors[0]);
        return;
      }

      setArtworkError("");

      const formData = new FormData();
      acceptedFiles.forEach((file) => formData.append("files", file));

      dispatch(uploadAFile(formData))
        .unwrap()
        .then((payload) => {
          if (Array.isArray(payload)) {
            setUploadedFiles((prev) => {
              const prevSafe = Array.isArray(prev) ? prev : [];
              const next = [...prevSafe, ...payload];

              const seen = new Set();
              return next.filter((x) => {
                const key = String(
                  x?.public_id || x?.url || x?.fileName || x?.original_filename || ""
                );
                if (!key) return true;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
              });
            });
          } else {
            setArtworkError("Unexpected response format from file upload");
          }
        })
        .catch(() => {
          setArtworkError(uploadErrorMsg || "Failed to upload file");
        });
    },
    [dispatch, validateArtworkFile, uploadErrorMsg]
  );

  const handleDeleteFile = useCallback(
    async (public_id) => {
      try {
        const result = await dispatch(deleteAFile(public_id)).unwrap();
        if (!result) throw new Error("Failed to delete file");

        setUploadedFiles((prev) =>
          Array.isArray(prev) ? prev.filter((f) => f?.public_id !== public_id) : []
        );
      } catch (e) {
        setArtworkError(e?.message || "Failed to delete file");
      }
    },
    [dispatch]
  );

  const confirm = useCallback(() => {
    if (!canConfirm) return;

    if (mustUploadFile && (!uploadedFiles || uploadedFiles.length === 0)) {
      setArtworkError("Artwork file is required to continue.");
      return;
    }

    const firstUpload =
      Array.isArray(uploadedFiles) && uploadedFiles.length ? uploadedFiles[0] : null;

    onConfirm?.({
      size,
      colorObj,
      hasArtwork: Boolean(hasArtwork),
      artworkUploads: hasArtwork ? uploadedFiles : [],
      artworkUpload: hasArtwork ? firstUpload : null,
      printInstructions: hasArtwork ? printInstructions : "",
      printSide: printSide || "",
    });

    onClose?.();
  }, [
    canConfirm,
    mustUploadFile,
    uploadedFiles,
    onConfirm,
    onClose,
    size,
    colorObj,
    hasArtwork,
    printInstructions,
    printSide,
  ]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "Enter") confirm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, confirm]);

  if (!open) return null;

  return (
    <div className="vt-overlay" role="dialog" aria-modal="true" onMouseDown={onOverlayMouseDown}>
      <div className="vt-card">
        <button className="vt-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <h3 className="vt-title">Important Selections</h3>

        <p className="vt-desc">
          Before adding <strong>{productTitle}</strong> to cart, please select{" "}
          {requireSize ? "a size" : ""}
          {requireSize && requireColor ? " and " : ""}
          {requireColor ? "a color" : ""}
          {hasPrintPricing && requirePrintSide
            ? `${requireSize || requireColor ? ", " : ""}a print side`
            : ""}
          {isPrintable
            ? `${
                requireSize ||
                requireColor ||
                (hasPrintPricing && requirePrintSide)
                  ? ", "
                  : ""
              }and confirm artwork`
            : ""}
          .
        </p>

        {/* SIZE */}
        {requireSize && sizesForUI?.length > 0 && (
          <div className="vt-block">
            <div className="vt-label">Size</div>

            <div className="vt-sizes">
              {sizesForUI.map((label) => {
                const active = size === label;
                return (
                  <button
                    key={label}
                    type="button"
                    className={`vt-sizeBtn ${active ? "active" : ""}`}
                    onClick={() => setSize(label)}
                    aria-pressed={active}
                    title={label}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* COLOR */}
        {requireColor && colors?.length > 0 && (
          <div className="vt-block">
            <div className="vt-label">Color</div>

            <div className="vt-colors">
              {colors.map((c) => {
                const id = c?.id || "";
                const label = c?.label || "";
                const hex = c?.hex || colorMap[label] || "#cccccc";
                const active = String(colorId) === String(id);

                return (
                  <button
                    key={String(id) || label}
                    type="button"
                    className={`vt-colorDot ${active ? "active" : ""}`}
                    style={{ background: hex }}
                    aria-label={label}
                    onClick={() => setColorId(id)}
                    title={label}
                  >
                    {active ? <span className="vt-check">✓</span> : null}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ✅ PRINT SIDE (shows discount based on qty + discountMinQty) */}
        {hasPrintPricing && requirePrintSide ? (
          <div className="vt-block">
            <div className="vt-label">Print Side</div>

            <div className="vt-yesNoRow" role="radiogroup" aria-label="Print side">
              <button
                type="button"
                className={`vt-ynBtn ${printSide === "oneSide" ? "active" : ""}`}
                aria-pressed={printSide === "oneSide"}
                onClick={() => setPrintSide("oneSide")}
              >
                One-side
              </button>

              <button
                type="button"
                className={`vt-ynBtn ${printSide === "twoSide" ? "active" : ""}`}
                aria-pressed={printSide === "twoSide"}
                onClick={() => setPrintSide("twoSide")}
              >
                Two-side
              </button>
            </div>

            <div className="vt-ynHint" style={{ marginTop: 8 }}>
              <div>
                <strong>Print type:</strong> {printPricing?.title || "—"}
              </div>

              <div>
                <strong>Quantity:</strong> {Number(qty || 1)}
              </div>

              {printDiscountMinQty ? (
                <div>
                  <strong>Discount from:</strong> Qty ≥ {printDiscountMinQty}{" "}
                  {printDiscountApplies ? (
                    <span style={{ color: "#0a7" }}>(applied)</span>
                  ) : (
                    <span style={{ color: "#777" }}>(not yet)</span>
                  )}
                </div>
              ) : null}

              <div>
                <strong>Unit print price:</strong>{" "}
                {printSide ? formatUGX(printUnit) : "Select a side"}
              </div>

              {printPreparePrice > 0 ? (
                <div>
                  <strong>Setup fee:</strong> {formatUGX(printPreparePrice)}{" "}
                  <span style={{ color: "#777" }}>
                    ({prepareAlreadyApplied ? "already applied in cart" : "charged once"})
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* ASK YES/NO FIRST */}
        {isPrintable && (
          <div className="vt-block">
            <div className="vt-label">Do you have artwork to upload?</div>

            <div className="vt-yesNoRow" role="radiogroup" aria-label="Artwork availability">
              <button
                type="button"
                className={`vt-ynBtn ${hasArtwork === true ? "active" : ""}`}
                aria-pressed={hasArtwork === true}
                onClick={() => setHasArtwork(true)}
              >
                Yes
              </button>

              <button
                type="button"
                className={`vt-ynBtn ${hasArtwork === false ? "active" : ""}`}
                aria-pressed={hasArtwork === false}
                onClick={() => setHasArtwork(false)}
              >
                No
              </button>
            </div>

            {hasArtwork === false ? (
              <div className="vt-ynHint">
                No problem — you can proceed without uploading. (We’ll contact you if needed.)
              </div>
            ) : null}
          </div>
        )}

        {/* UPLOAD + INSTRUCTIONS ONLY WHEN YES */}
        {showUploadUI && (
          <div className="vt-block">
            <div className="vt-label">Upload Artwork</div>

            <div className="vt-uploadHint">
              Allowed: <strong>{allowedExts.join(", ")}</strong> • Max: <strong>{maxMB}MB</strong>
            </div>

            {mergedPrintSpec?.instructions ? (
              <div className="vt-uploadPreset">
                <strong>Product instructions:</strong> {mergedPrintSpec.instructions}
              </div>
            ) : null}

            <Dropzone onDrop={handleFileDrop}>
              {({ getRootProps, getInputProps }) => (
                <div
                  {...getRootProps({
                    className: `vt-drop ${artworkError ? "error" : ""}`,
                    role: "button",
                    tabIndex: 0,
                    "aria-label": "Upload artwork file",
                  })}
                >
                  <input {...getInputProps()} accept={acceptAttr} />

                  <div className="vt-dropIcon" aria-hidden="true">
                    <FiUploadCloud />
                  </div>

                  <div className="vt-dropText">
                    <div className="vt-dropTitle">
                      {uploading
                        ? "Uploading..."
                        : uploadedFiles.length
                        ? "Files uploaded"
                        : "Click to upload artwork"}
                    </div>

                    <div className="vt-dropSub">
                      {uploadedFiles.length
                        ? `${uploadedFiles.length} file(s) uploaded`
                        : "PDF, PNG, JPG, JPEG, CDR, AI"}
                    </div>
                  </div>
                </div>
              )}
            </Dropzone>

            {uploading ? (
              <div className="vt-uploadProgress">
                <div className="vt-uploadProgressTop">
                  <span>Uploading...</span>
                  <span>{Math.min(100, Math.max(0, uploadProgress))}%</span>
                </div>
                <div className="vt-uploadBar">
                  <div
                    className="vt-uploadBarFill"
                    style={{ width: `${Math.min(100, Math.max(0, uploadProgress))}%` }}
                  />
                </div>
              </div>
            ) : null}

            {uploadedFiles.length ? (
              <div className="vt-filesList">
                {uploadedFiles.map((file, idx) => {
                  const name =
                    file?.fileName || file?.original_filename || file?.public_id || `File ${idx + 1}`;
                  const pid = file?.public_id;

                  return (
                    <div className="vt-filePill" key={pid || `${name}-${idx}`}>
                      <div className="vt-filePillLeft">
                        <FiFileText />
                        <span className="vt-filePillName" title={name}>
                          {name}
                        </span>
                      </div>

                      {pid ? (
                        <button
                          type="button"
                          className="vt-filePillRemove"
                          onClick={() => handleDeleteFile(pid)}
                          aria-label="Remove file"
                          title="Remove"
                          disabled={uploading}
                        >
                          <FiX />
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}

            {artworkError ? <div className="vt-uploadError">{artworkError}</div> : null}

            {mustUploadFile && !uploadedFiles.length ? (
              <div className="vt-uploadError">Artwork file is required to continue.</div>
            ) : null}

            <div className="vt-instrCard">
              <div className="vt-instrTop">
                <span className="vt-instrLabel">Printing Instructions</span>
                <span className="vt-instrBadge">Optional</span>
              </div>

              <textarea
                className="vt-instrTextarea"
                rows={4}
                placeholder="e.g. Print front only, keep margins 5mm, CMYK, no crop marks..."
                value={printInstructions}
                onChange={(e) => setPrintInstructions(e.target.value)}
              />

              <div className="vt-instrSmall">Add any notes for your designer/printer.</div>
            </div>
          </div>
        )}

        <button className="vt-confirm" disabled={!canConfirm || uploading} onClick={confirm}>
          {uploading ? "Uploading..." : "Add to Cart"}
        </button>
      </div>
    </div>
  );
};

export default VariantToast;
